import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../../shared/providers/AuthProvider';
import { parseQueryString } from '../../shared/utilities/queryString';

import { NormalizedAgents, SerializedAgent, Agent } from './types';
import { fetchAgents } from './api';

export const getAgentsQueryKey = () => ['agents'];

export const useGetAgents = (
    options?: Omit<UseQueryOptions<SerializedAgent, Error, NormalizedAgents>, 'queryKey'>,
) => {
    return useQuery({
        queryKey: getAgentsQueryKey(),
        queryFn: fetchAgents,
        select: (data) => data.agent,
        ...options,
    });
};

export const useGetLoggedAgent = (): Agent | null => {
    const { data: agents } = useGetAgents();
    const { userId } = useAuth();

    return useMemo(() => {
        if (typeof userId === 'undefined' || userId === null || !agents) {
            return null;
        }
        return agents[userId.toString()] || null;
    }, [agents, userId]);
};

export const useGetSelectAgents = (
    options?: Omit<UseQueryOptions<SerializedAgent, Error, NormalizedAgents>, 'queryKey'>,
) => {
    const { data: agents } = useGetAgents(options);

    return useMemo(() => {
        if (!agents) return [];

        return Object.values(agents).map((agent) => ({
            label: agent.name,
            value: agent.id,
            photoUrl: agent.photoUrl,
        }));
    }, [agents]);
};

export const useCanDisplayAvatar = (agentId: string | undefined) => {
    const location = useLocation();
    const { data: agents } = useGetAgents();

    return useMemo(() => {
        if (!agents || agentId === undefined) return false;

        const queryAgentId = parseQueryString(location.search).agentId;

        // eslint-disable-next-line sonarjs/different-types-comparison -- queryAgentId is a string, agentId is typed as number; comparison is intentional
        return queryAgentId !== undefined && agents[agentId] !== undefined;
    }, [location.search, agents, agentId]);
};
